import React, { useState, useEffect } from 'react';
import { Phone, Mail, MapPin, Wrench, Shield, Clock, CheckCircle2, Star, Instagram, Facebook } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Home = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Review state
  const [reviews, setReviews] = useState([]);
  const [reviewForm, setReviewForm] = useState({
    name: '',
    email: '',
    rating: 5,
    review_text: '',
    service_type: ''
  });
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);

  // Fetch approved reviews
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/reviews/approved`);
        setReviews(response.data);
      } catch (error) {
        console.error('Error fetching reviews:', error);
      }
    };
    fetchReviews();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact`, formData);
      
      if (response.data.success) {
        toast.success('Quote request received! We\'ll contact you shortly.');
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit. Please try calling us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingReview(true);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/reviews`, reviewForm);
      
      if (response.data.success) {
        toast.success('Thank you for your review! It will be posted after approval.');
        setReviewForm({ name: '', email: '', rating: 5, review_text: '', service_type: '' });
        setShowReviewForm(false);
      }
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit review. Please try again.');
    } finally {
      setIsSubmittingReview(false);
    }
  };

  const services = [
    {
      icon: <Wrench className="w-8 h-8" />,
      title: 'Mobile Welding',
      description: 'On-site welding services at your location. We come to you with fully equipped mobile units, available 24/7.'
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Custom Fabrication',
      description: 'Custom metal fabrication for industrial, commercial, and agricultural applications.'
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: '24/7 Emergency Repairs',
      description: 'Available around the clock for urgent welding and repair needs. Fast response times when you need it most.'
    }
  ];

  const galleryImages = [
    'https://customer-assets.emergentagent.com/job_metal-masters-12/artifacts/jte8erj0_9D89C059-B160-42FB-90DF-8B89DDA0C4A2.JPG',
    'https://customer-assets.emergentagent.com/job_metal-masters-12/artifacts/r6y0zfjx_59F3224A-E25E-4850-96C4-CA36D302DC9B.JPG',
    'https://customer-assets.emergentagent.com/job_metal-masters-12/artifacts/cxppylwl_3E684EA9-0353-44FC-9A11-E7AC7F0A0421.JPG',
    'https://customer-assets.emergentagent.com/job_metal-masters-12/artifacts/54wcwxwb_69F8D888-6115-404A-9F6A-C50C200DAC9D.JPG',
    'https://customer-assets.emergentagent.com/job_metal-masters-12/artifacts/w0iwarpn_15848EC2-5268-4EF0-9B4B-2CC888665E0B.JPG',
    'https://customer-assets.emergentagent.com/job_metal-masters-12/artifacts/humyjfie_DEAF2F0A-26D8-4ECE-99FA-A9DED43E21F9.JPG',
    'https://customer-assets.emergentagent.com/job_metal-masters-12/artifacts/r8pwbdan_IMG_0069%20%281%29.JPG',
    'https://customer-assets.emergentagent.com/job_metal-masters-12/artifacts/rrdyfncj_IMG_4588.JPG',
    'https://customer-assets.emergentagent.com/job_metal-masters-12/artifacts/plst3wwk_IMG_4341.HEIC',
    'https://customer-assets.emergentagent.com/job_metal-masters-12/artifacts/6dd33ans_IMG_4340.HEIC',
    'https://customer-assets.emergentagent.com/job_metal-masters-12/artifacts/zoawsh5k_IMG20200324092423.jpg'
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0b]/95 backdrop-blur-md border-b border-[#D4AF37]/20">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_metal-masters-12/artifacts/ng84v4iu_profile%20attempt.png" 
              alt="Ryder Mobile Welding" 
              className="h-12 w-auto cursor-pointer"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            />
          </div>
          <div className="hidden md:flex items-center space-x-8">
            <a href="#services" className="hover:text-[#D4AF37] transition-colors">Services</a>
            <a href="#about" className="hover:text-[#D4AF37] transition-colors">About</a>
            <a href="#gallery" className="hover:text-[#D4AF37] transition-colors">Gallery</a>
            <a href="#contact" className="hover:text-[#D4AF37] transition-colors">Contact</a>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden sm:flex items-center gap-3">
              <a 
                href="https://www.instagram.com/rydermobilewelding/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-[#D4AF37] transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a 
                href="https://www.facebook.com/profile.php?id=61588157487713" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-[#D4AF37] transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
            </div>
            <a href="tel:0413448470">
              <Button className="bg-[#D4AF37] text-black hover:bg-[#C4A037] font-bold">
                <Phone className="w-4 h-4 mr-2" />
                Call Now
              </Button>
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a1b] to-[#0a0a0b]"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23D4AF37' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
        </div>
        
        <div className="container mx-auto max-w-5xl relative z-10 text-center">
          <div className="mb-8 flex justify-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_metal-masters-12/artifacts/ng84v4iu_profile%20attempt.png" 
              alt="Ryder Mobile Welding" 
              className="h-40 w-auto animate-fade-in"
            />
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold mb-6 tracking-tight">
            Ryder Mobile <span className="text-[#D4AF37]">Welding</span>
          </h1>
          
          <p className="text-2xl md:text-3xl text-gray-300 mb-4 font-semibold">
            Professional Mobile Welding & Fabrication Services
          </p>
          
          <p className="text-lg md:text-xl text-gray-400 mb-8">
            Servicing Commercial, Industrial & Rural Clients<br />
            Fully Insured • 24/7 Emergency Repairs • Fast Response • Quality Workmanship
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
            <a href="tel:0413448470">
              <Button size="lg" className="w-full sm:w-auto bg-[#D4AF37] text-black hover:bg-[#C4A037] font-bold text-lg px-8 py-6">
                <Phone className="w-5 h-5 mr-2" />
                Call Now – 0413 448 470
              </Button>
            </a>
            <a href="#contact">
              <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black font-bold text-lg px-8 py-6">
                Request a Quote
              </Button>
            </a>
          </div>
          
          <div className="flex flex-col md:flex-row gap-6 justify-center items-center text-gray-300">
            <div className="flex items-center gap-2">
              <Phone className="w-5 h-5 text-[#D4AF37]" />
              <a href="tel:0413448470" className="hover:text-[#D4AF37] transition-colors">0413 448 470</a>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#D4AF37]" />
              <a href="mailto:rydermobilewelding@gmail.com" className="hover:text-[#D4AF37] transition-colors">rydermobilewelding@gmail.com</a>
            </div>
            <div className="flex items-center gap-3">
              <a 
                href="https://www.instagram.com/rydermobilewelding/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-[#D4AF37] transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="w-6 h-6" />
              </a>
              <a 
                href="https://www.facebook.com/profile.php?id=61588157487713" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-[#D4AF37] transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="w-6 h-6" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-4 bg-[#0f0f10]">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-4">
            Our <span className="text-[#D4AF37]">Services</span>
          </h2>
          <p className="text-center text-gray-400 mb-12 max-w-2xl mx-auto">
            Professional welding and fabrication services delivered to your location
          </p>
          
          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <Card key={index} className="bg-[#1a1a1b] border-[#D4AF37]/20 hover:border-[#D4AF37] transition-all duration-300 hover:transform hover:scale-105">
                <CardContent className="p-8 text-center">
                  <div className="flex justify-center mb-4 text-[#D4AF37]">
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-white">{service.title}</h3>
                  <p className="text-gray-400">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-4 bg-[#0a0a0b]">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-12">
            Why Choose <span className="text-[#D4AF37]">Ryder Mobile Welding</span>
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="flex gap-4">
                <CheckCircle2 className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-bold mb-2">Fully Mobile Service</h3>
                  <p className="text-gray-400">We bring professional welding equipment directly to your site, saving you time and money.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <CheckCircle2 className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-bold mb-2">Experienced & Qualified</h3>
                  <p className="text-gray-400">Certified welder with extensive experience in commercial, industrial, and rural applications.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <CheckCircle2 className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-bold mb-2">Quality Workmanship</h3>
                  <p className="text-gray-400">We take pride in delivering high-quality welds that meet industry standards and exceed expectations.</p>
                </div>
              </div>
            </div>
            <div className="space-y-6">
              <div className="flex gap-4">
                <CheckCircle2 className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-bold mb-2">24/7 Availability</h3>
                  <p className="text-gray-400">Available around the clock for emergency welding services. We're here when you need us most, day or night.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <CheckCircle2 className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-bold mb-2">Competitive Pricing</h3>
                  <p className="text-gray-400">Fair, transparent pricing with no hidden costs. Get a free quote for your project today.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <CheckCircle2 className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-bold mb-2">Versatile Capabilities</h3>
                  <p className="text-gray-400">From small repairs to large fabrication projects, we handle jobs of all sizes and complexities.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="py-20 px-4 bg-[#0f0f10]">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-4">
            Our <span className="text-[#D4AF37]">Work</span>
          </h2>
          <p className="text-center text-gray-400 mb-12">
            Quality craftsmanship in every project we undertake
          </p>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {galleryImages.map((image, index) => (
              <div 
                key={index} 
                className="aspect-square overflow-hidden rounded-lg border-2 border-[#D4AF37]/20 hover:border-[#D4AF37] transition-all duration-300 cursor-pointer group"
              >
                <img 
                  src={image} 
                  alt={`Welding project ${index + 1}`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4 bg-[#0a0a0b]">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-4">
            What Our <span className="text-[#D4AF37]">Clients Say</span>
          </h2>
          <p className="text-center text-gray-400 mb-12">
            {reviews.length > 0 ? 'Real reviews from our valued clients' : 'Be the first to leave a review!'}
          </p>
          
          {reviews.length > 0 ? (
            <div className="grid md:grid-cols-3 gap-8 mb-8">
              {reviews.map((review) => (
                <Card key={review.id} className="bg-[#1a1a1b] border-[#D4AF37]/20">
                  <CardContent className="p-6">
                    <div className="flex gap-1 mb-4">
                      {[...Array(5)].map((_, i) => (
                        <Star 
                          key={i} 
                          className={`w-5 h-5 ${i < review.rating ? 'fill-[#D4AF37] text-[#D4AF37]' : 'text-gray-600'}`} 
                        />
                      ))}
                    </div>
                    <p className="text-gray-300 mb-4 italic">"{review.review_text}"</p>
                    <p className="text-[#D4AF37] font-bold">{review.name}</p>
                    {review.service_type && (
                      <p className="text-gray-500 text-sm">{review.service_type}</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center text-gray-400 mb-8">
              <p>No reviews yet. Be the first to share your experience!</p>
            </div>
          )}
          
          {/* Review Form Toggle */}
          <div className="text-center">
            {!showReviewForm ? (
              <Button 
                onClick={() => setShowReviewForm(true)}
                className="bg-[#D4AF37] text-black hover:bg-[#C4A037] font-bold"
              >
                Leave a Review
              </Button>
            ) : (
              <Card className="bg-[#1a1a1b] border-[#D4AF37]/20 max-w-2xl mx-auto">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold mb-6 text-white">Share Your Experience</h3>
                  <form onSubmit={handleReviewSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium mb-2 text-white">Name *</label>
                        <Input 
                          required
                          value={reviewForm.name}
                          onChange={(e) => setReviewForm({...reviewForm, name: e.target.value})}
                          className="bg-[#0a0a0b] border-[#D4AF37]/30 focus:border-[#D4AF37] text-white"
                          placeholder="Your name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2 text-white">Email *</label>
                        <Input 
                          type="email"
                          required
                          value={reviewForm.email}
                          onChange={(e) => setReviewForm({...reviewForm, email: e.target.value})}
                          className="bg-[#0a0a0b] border-[#D4AF37]/30 focus:border-[#D4AF37] text-white"
                          placeholder="your@email.com"
                        />
                      </div>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium mb-2 text-white">Rating *</label>
                        <div className="flex gap-2">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              className={`w-8 h-8 cursor-pointer transition-all ${
                                star <= reviewForm.rating 
                                  ? 'fill-[#D4AF37] text-[#D4AF37]' 
                                  : 'text-gray-600 hover:text-[#D4AF37]'
                              }`}
                              onClick={() => setReviewForm({...reviewForm, rating: star})}
                            />
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2 text-white">Service Type</label>
                        <Input 
                          value={reviewForm.service_type}
                          onChange={(e) => setReviewForm({...reviewForm, service_type: e.target.value})}
                          className="bg-[#0a0a0b] border-[#D4AF37]/30 focus:border-[#D4AF37] text-white"
                          placeholder="e.g., Mobile Welding"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2 text-white">Your Review *</label>
                      <Textarea 
                        required
                        value={reviewForm.review_text}
                        onChange={(e) => setReviewForm({...reviewForm, review_text: e.target.value})}
                        className="bg-[#0a0a0b] border-[#D4AF37]/30 focus:border-[#D4AF37] text-white min-h-32"
                        placeholder="Tell us about your experience..."
                        maxLength={500}
                      />
                      <p className="text-xs text-gray-500 mt-1">{reviewForm.review_text.length}/500 characters</p>
                    </div>
                    
                    <div className="flex gap-4">
                      <Button 
                        type="submit" 
                        disabled={isSubmittingReview}
                        className="flex-1 bg-[#D4AF37] text-black hover:bg-[#C4A037] font-bold"
                      >
                        {isSubmittingReview ? 'Submitting...' : 'Submit Review'}
                      </Button>
                      <Button 
                        type="button"
                        onClick={() => setShowReviewForm(false)}
                        variant="outline"
                        className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black"
                      >
                        Cancel
                      </Button>
                    </div>
                    
                    <p className="text-sm text-gray-500 text-center">
                      Your review will be posted after approval. Email address will not be displayed.
                    </p>
                  </form>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section className="py-20 px-4 bg-[#0a0a0b]">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-4">
            Our <span className="text-[#D4AF37]">Location</span>
          </h2>
          <p className="text-center text-gray-400 mb-12">
            Based in Uki, NSW - Servicing the Northern Rivers and surrounding areas
          </p>
          
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <MapPin className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-bold mb-2">Service Area</h3>
                  <p className="text-gray-400">
                    Uki, NSW 2484<br />
                    Servicing Northern Rivers, Tweed, Byron Bay, and surrounding regions
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <Phone className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-bold mb-2">Contact Us</h3>
                  <p className="text-gray-400">
                    <a href="tel:0413448470" className="hover:text-[#D4AF37] transition-colors">
                      0413 448 470
                    </a>
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <Clock className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-bold mb-2">Mobile Service</h3>
                  <p className="text-gray-400">
                    We come to you! Fast response times for on-site welding and fabrication services.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="rounded-lg overflow-hidden border-2 border-[#D4AF37]/20 h-[400px]">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d28283.15698542937!2d153.28719!3d-28.40861!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6b9112b1f3b3b3b3%3A0x0!2sUki%20NSW%202484!5e0!3m2!1sen!2sau!4v1639500000000!5m2!1sen!2sau"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Ryder Mobile Welding Location - Uki, NSW 2484"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact" className="py-20 px-4 bg-[#0f0f10]">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-4">
            Request a <span className="text-[#D4AF37]">Quote</span>
          </h2>
          <p className="text-center text-gray-400 mb-12">
            Get in touch for a free quote on your welding project
          </p>
          
          <Card className="bg-[#1a1a1b] border-[#D4AF37]/20">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">Name *</label>
                    <Input 
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="bg-[#0a0a0b] border-[#D4AF37]/30 focus:border-[#D4AF37] text-white"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Email *</label>
                    <Input 
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="bg-[#0a0a0b] border-[#D4AF37]/30 focus:border-[#D4AF37] text-white"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">Phone *</label>
                    <Input 
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="bg-[#0a0a0b] border-[#D4AF37]/30 focus:border-[#D4AF37] text-white"
                      placeholder="0400 000 000"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Service Type</label>
                    <Input 
                      value={formData.service}
                      onChange={(e) => setFormData({...formData, service: e.target.value})}
                      className="bg-[#0a0a0b] border-[#D4AF37]/30 focus:border-[#D4AF37] text-white"
                      placeholder="e.g., Mobile Welding, Fabrication"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Message *</label>
                  <Textarea 
                    required
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    className="bg-[#0a0a0b] border-[#D4AF37]/30 focus:border-[#D4AF37] text-white min-h-32"
                    placeholder="Tell us about your project..."
                  />
                </div>
                
                <Button 
                  type="submit" 
                  size="lg" 
                  disabled={isSubmitting}
                  className="w-full bg-[#D4AF37] text-black hover:bg-[#C4A037] font-bold text-lg"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Quote Request'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0a0a0b] border-t border-[#D4AF37]/20 py-12 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_metal-masters-12/artifacts/ng84v4iu_profile%20attempt.png" 
                alt="Ryder Mobile Welding" 
                className="h-20 w-auto mb-4"
              />
              <p className="text-gray-400">
                Professional mobile welding and fabrication services for commercial, industrial, and rural clients.
              </p>
            </div>
            
            <div>
              <h3 className="text-lg font-bold mb-4 text-[#D4AF37]">Quick Links</h3>
              <ul className="space-y-2">
                <li><a href="#services" className="text-gray-400 hover:text-[#D4AF37] transition-colors">Services</a></li>
                <li><a href="#about" className="text-gray-400 hover:text-[#D4AF37] transition-colors">About</a></li>
                <li><a href="#gallery" className="text-gray-400 hover:text-[#D4AF37] transition-colors">Gallery</a></li>
                <li><a href="#contact" className="text-gray-400 hover:text-[#D4AF37] transition-colors">Contact</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-bold mb-4 text-[#D4AF37]">Contact Us</h3>
              <div className="space-y-3">
                <a href="tel:0413448470" className="flex items-center gap-2 text-gray-400 hover:text-[#D4AF37] transition-colors">
                  <Phone className="w-5 h-5" />
                  0413 448 470
                </a>
                <a href="mailto:rydermobilewelding@gmail.com" className="flex items-center gap-2 text-gray-400 hover:text-[#D4AF37] transition-colors">
                  <Mail className="w-5 h-5" />
                  rydermobilewelding@gmail.com
                </a>
                <div className="flex items-center gap-4 pt-2">
                  <a 
                    href="https://www.instagram.com/rydermobilewelding/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-[#D4AF37] transition-colors"
                    aria-label="Instagram"
                  >
                    <Instagram className="w-6 h-6" />
                  </a>
                  <a 
                    href="https://www.facebook.com/profile.php?id=61588157487713" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-[#D4AF37] transition-colors"
                    aria-label="Facebook"
                  >
                    <Facebook className="w-6 h-6" />
                  </a>
                </div>
              </div>
            </div>
          </div>
          
          <div className="border-t border-[#D4AF37]/20 pt-8 text-center text-gray-500">
            <p>&copy; {new Date().getFullYear()} Ryder Mobile Welding. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
